import React, { useState } from 'react';
import { Check, ShieldCheck } from 'lucide-react';
import { HUNTR_PLANS, HUNTR_INCLUDED, ANNUAL_FACTOR } from '@/data/huntr';
import { CURRENCIES } from '@/data/currencies';
import CurrencyToggle from '@/components/CurrencyToggle';
import HuntrPlanCard from '@/components/huntr/HuntrPlanCard';

export default function HuntrPricing() {
  const [annual, setAnnual] = useState(false);
  const [code, setCode] = useState('USD');
  const currency = CURRENCIES.find((c) => c.code === code);
  const priceFor = (usd) => {
    const raw = usd * currency.rate * (annual ? ANNUAL_FACTOR : 1);
    return currency.code === 'ZAR' ? Math.round(raw / 100) * 100 : Math.round(raw / 10) * 10;
  };

  return (
    <section id="huntr-pricing" className="bg-[#0B0B0B] border-t border-white/10 py-16 lg:py-24">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#3B82F6]">Huntr Pricing</p>
        <h2 className="mt-4 font-display font-bold text-white text-3xl lg:text-[2.75rem] tracking-tight max-w-3xl leading-tight">
          Pay for guaranteed leads, not software seats.
        </h2>
        <p className="mt-4 text-white/55 font-light max-w-2xl leading-relaxed">
          Four tiers, one engine. Every plan includes the full Huntr platform, sending infrastructure and a senior strategist — backed by our 60-day money-back guarantee.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="inline-flex items-center gap-1 p-1 rounded-lg border border-white/10 bg-white/[0.03]">
            {[{ v: false, l: 'Monthly' }, { v: true, l: 'Annual' }].map((o) => (
              <button
                key={o.l}
                type="button"
                onClick={() => setAnnual(o.v)}
                className={`px-4 h-9 rounded-md text-xs font-semibold transition-colors ${annual === o.v ? 'bg-[#2563EB] text-white' : 'text-white/50 hover:text-white'}`}
              >
                {o.l}
                {o.v && <span className="ml-2 text-[10px] text-emerald-300">Save 17%</span>}
              </button>
            ))}
          </div>
          <CurrencyToggle value={code} onChange={setCode} />
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {HUNTR_PLANS.map((p) => (
            <HuntrPlanCard key={p.label} plan={p} price={priceFor(p.price)} symbol={currency.symbol} annual={annual} />
          ))}
        </div>
        {code !== 'USD' && (
          <p className="mt-4 text-xs text-white/35">Indicative conversion. All subscriptions are billed in USD.</p>
        )}

        <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-7 lg:p-9 grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-12">
          <div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-lg border border-[#3B82F6]/30 bg-[#2563EB]/10">
              <ShieldCheck className="w-3.5 h-3.5 text-[#3B82F6]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/80">60-Day Results Guarantee</span>
            </div>
            <p className="mt-5 text-white/60 text-sm leading-relaxed">
              Guaranteed hot leads per working day. If Huntr does not deliver your contracted volume, you get your money back — the risk sits with us, not with your budget.
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-5">Included in every plan</p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
              {HUNTR_INCLUDED.map((t) => (
                <li key={t} className="flex gap-2.5 text-sm text-white/70 leading-relaxed">
                  <Check className="w-4 h-4 text-[#3B82F6] shrink-0 mt-0.5" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}